/**
 * Vajra IMS Client-Side SWR In-Memory Cache Store
 * Provides key-based caching, configurable TTLs, in-flight promise deduplication,
 * stale-while-revalidate semantics, and granular invalidation.
 */

// In-memory data structures
const cacheStore = new Map();
const inflightStore = new Map();
const subscribers = new Map();

// Default TTL configurations (in milliseconds)
export const DEFAULT_TTLS = {
  '/api/dashboard': 60 * 1000,    // 60 seconds
  '/api/billing': 60 * 1000,      // 60 seconds
  '/api/invoices': 60 * 1000,     // 60 seconds
  '/api/certificates': 60 * 1000, // 60 seconds
  '/api/payments': 60 * 1000,     // 60 seconds
  '/api/reports': 60 * 1000,      // 60 seconds
  '/api/banks': 120 * 1000,       // 120 seconds
  '/api/agents': 120 * 1000,      // 120 seconds
  '/api/cms-clients': 120 * 1000, // 120 seconds
  '/api/settings': 300 * 1000,    // 300 seconds
  DEFAULT: 60 * 1000,             // 60 seconds fallback
};

/**
 * Resolves the appropriate TTL for a given cache key
 */
export function resolveTTL(key) {
  if (!key) return DEFAULT_TTLS.DEFAULT;
  for (const [prefix, ttl] of Object.entries(DEFAULT_TTLS)) {
    if (prefix !== 'DEFAULT' && key.startsWith(prefix)) {
      return ttl;
    }
  }
  return DEFAULT_TTLS.DEFAULT;
}

/**
 * Creates a deterministic, query-sorted cache key
 */
export function createCacheKey(urlOrPrefix, params = null) {
  if (!params || typeof params !== 'object' || Object.keys(params).length === 0) {
    return urlOrPrefix;
  }
  const sortedKeys = Object.keys(params).sort();
  const queryParts = [];
  for (const k of sortedKeys) {
    const v = params[k];
    if (v !== undefined && v !== null && v !== '') {
      queryParts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    }
  }
  if (queryParts.length === 0) return urlOrPrefix;
  const separator = urlOrPrefix.includes('?') ? '&' : '?';
  return `${urlOrPrefix}${separator}${queryParts.join('&')}`;
}

/**
 * Synchronously retrieves a cache entry
 */
export function getCache(key) {
  if (!key) return { data: undefined, isStale: true, exists: false };
  const entry = cacheStore.get(key);
  if (!entry) return { data: undefined, isStale: true, exists: false };
  const now = Date.now();
  const isStale = (now - entry.timestamp) > entry.ttl;
  return {
    data: entry.data,
    isStale,
    exists: true,
    timestamp: entry.timestamp,
    ttl: entry.ttl,
  };
}

/**
 * Sets a cache entry and notifies subscribers
 */
export function setCache(key, data, customTtl = null) {
  if (!key) return;
  const ttl = customTtl || resolveTTL(key);
  const entry = { data, timestamp: Date.now(), ttl };
  cacheStore.set(key, entry);
  notifySubscribers(key, data);
  return entry;
}

/**
 * Subscribes a listener callback to cache updates for a specific key
 */
export function subscribe(key, callback) {
  if (!key || typeof callback !== 'function') return () => {};
  if (!subscribers.has(key)) {
    subscribers.set(key, new Set());
  }
  subscribers.get(key).add(callback);
  return () => {
    const subs = subscribers.get(key);
    if (subs) {
      subs.delete(callback);
      if (subs.size === 0) subscribers.delete(key);
    }
  };
}

/**
 * Broadcasts cache update to all subscribers for a key
 */
function notifySubscribers(key, data) {
  const subs = subscribers.get(key);
  if (subs) {
    subs.forEach(cb => {
      try {
        cb(data);
      } catch (err) {
        console.error(`[Cache] Subscriber error for key "${key}":`, err);
      }
    });
  }
}

/**
 * Fetches data with in-flight deduplication and SWR caching
 */
export async function fetchWithCache(key, fetcher, options = {}) {
  if (!key) throw new Error('[Cache] Key is required for fetchWithCache');
  const { forceRevalidate = false, ttl = null } = options;
  const cached = getCache(key);

  // Return active in-flight request if one exists
  if (inflightStore.has(key)) {
    return inflightStore.get(key);
  }

  // Return fresh cache if not forcing revalidation
  if (cached.exists && !cached.isStale && !forceRevalidate) {
    return cached.data;
  }

  // Initiate deduplicated fetch
  const promise = (async () => {
    try {
      const res = await fetcher();
      const payload = (res && typeof res === 'object' && 'data' in res) ? res.data : res;
      setCache(key, payload, ttl);
      return payload;
    } finally {
      inflightStore.delete(key);
    }
  })();

  inflightStore.set(key, promise);
  return promise;
}

/**
 * Marks an exact cache key as stale
 */
export function invalidate(key) {
  if (!key) return;
  const entry = cacheStore.get(key);
  if (entry) {
    entry.timestamp = 0; // Mark stale for instant SWR revalidation
  }
}

/**
 * Completely removes a key from cache and inflight store
 */
export function deleteCache(key) {
  if (!key) return;
  cacheStore.delete(key);
  inflightStore.delete(key);
}

/**
 * Marks all cache keys matching a pattern/prefix as stale
 */
export function invalidatePattern(pattern) {
  if (!pattern) return;
  const isRegExp = pattern instanceof RegExp;
  for (const key of cacheStore.keys()) {
    const match = isRegExp ? pattern.test(key) : key.startsWith(pattern) || key.includes(pattern);
    if (match) {
      const entry = cacheStore.get(key);
      if (entry) {
        entry.timestamp = 0; // Stale mark ensures next access revalidates
      }
    }
  }
}

/**
 * Clears the entire cache and in-flight request store
 */
export function clearCache() {
  cacheStore.clear();
  inflightStore.clear();
}

/**
 * Mutation Invalidation Map
 * Rule: /api/invoices: [/api/invoices, /api/certificates, /api/payments, /api/dashboard, /api/billing, /api/reports]
 */
export const MUTATION_INVALIDATION_MAP = {
  '/api/banks': ['/api/banks', '/api/dashboard'],
  '/api/agents': ['/api/agents', '/api/banks', '/api/dashboard', '/api/payments'],
  '/api/certificates': ['/api/certificates', '/api/dashboard', '/api/billing', '/api/reports'],
  '/api/public/upload': ['/api/certificates', '/api/dashboard'],
  '/api/invoices': ['/api/invoices', '/api/certificates', '/api/payments', '/api/dashboard', '/api/billing', '/api/reports'],
  '/api/payments': ['/api/payments', '/api/invoices', '/api/dashboard', '/api/reports'],
  '/api/cms-clients': ['/api/cms-clients', '/api/banks'],
  '/api/settings': ['/api/settings'],
  '/api/admin': ['/api/banks', '/api/agents', '/api/certificates', '/api/invoices', '/api/payments', '/api/dashboard', '/api/cms-clients'],
};

/**
 * Automatically invalidates dependent cache patterns based on a mutation URL
 */
export function invalidateOnMutation(url) {
  if (!url) return;
  try {
    const path = url.startsWith('http') ? new URL(url).pathname : url;
    for (const [prefix, patterns] of Object.entries(MUTATION_INVALIDATION_MAP)) {
      if (path.startsWith(prefix) || path.includes(prefix)) {
        for (const pattern of patterns) {
          invalidatePattern(pattern);
        }
      }
    }
  } catch {
    for (const [prefix, patterns] of Object.entries(MUTATION_INVALIDATION_MAP)) {
      if (url.includes(prefix)) {
        for (const pattern of patterns) {
          invalidatePattern(pattern);
        }
      }
    }
  }
}

// Export default singleton object
const cache = {
  get: getCache,
  set: setCache,
  fetchWithCache,
  subscribe,
  invalidate,
  delete: deleteCache,
  invalidatePattern,
  invalidateOnMutation,
  clear: clearCache,
  createKey: createCacheKey,
  resolveTTL,
};

export default cache;
